import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../utils/i18n';
import Modal from './ui/Modal';
import { flagInvalidField } from '../utils/formValidation';
import { useToast } from './ToastProvider';
import { useParams, useNavigate } from 'react-router-dom';
import { Ticket, Plus, Search, Users, Calendar, AlertCircle, CheckCircle, Clock, AlertTriangle, X } from 'lucide-react';
import apiService from '../services/apiService';
import TicketDetail from './TicketDetail';
import TicketContextMenu from './TicketContextMenu';
import TicketMergeModal from './TicketMergeModal';
import TicketCustomerField from './TicketCustomerField';
import CalendarTicketTitle from './CalendarTicketTitle';
import useAutoCache from '../hooks/useAutoCache';
import { CalenderMarkersDisplay, CalenderMarkerHeaderSelect } from '../utils/calenderMarkerUtils';
import { isHiddenByStatus } from '../utils/ticketFilters';
import { ticketPasstZurSuche } from '../utils/suchFilter'; // #572
import '../styles/TicketView.css';
import useIsMobile from '../hooks/useIsMobile';

const TicketView = () => {
  // W6 (#16): TicketDetail bekommt auf schmalen Screens das kompakte Mobil-Layout
  const isMobileScreen = useIsMobile();
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { pushToast } = useToast();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'normal',
    assignedTo: '',
    category: '',
    tags: [],
    calenderMarker: null,
    customerId: null,
    contactId: null
  });
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);

  useEffect(() => {
    loadAvailableEmails();
    // Load current user for assignee comparisons
    (async () => {
      try {
        const userResponse = await apiService.getCurrentUser();
        setCurrentUser(userResponse.user || userResponse);
      } catch (e) {
        console.warn('Could not load current user:', e);
      }
    })();
  }, []);

  const loadAvailableEmails = async () => {
    try {
      const emailsResponse = await apiService.getAvailableEmails();
      setAvailableEmails(emailsResponse.emails || []);
    } catch (error) {
      console.error('Error loading available emails:', error);
    }
  };

  // Handle ticket data updates from useAutoCache
  const handleTicketUpdate = useCallback((newTickets) => {
    setTickets(newTickets);
    setLoading(false);
  }, []);

  // W0-6: memoisiert — als Inline-Literal bekam useAutoCache bei jedem Render
  // eine neue Objekt-Identität und löste damit einen Dauer-Refetch aus.
  const ticketsFilters = React.useMemo(
    () => (showCompleted ? { includeArchived: '1' } : {}),
    [showCompleted]
  );

  // Setup automatic cache management for tickets (same as Dashboard)
  const { manualRefresh: manualRefreshTickets } = useAutoCache({
    type: 'tickets',
    enabled: true,
    onDataUpdate: handleTicketUpdate,
    checkInterval: 60000, // Check every 60 seconds (reduced from 5s to prevent pool exhaustion)
    ticketsFilters
  });

  // Note: Live loading via useAutoCache handles initial and periodic refreshes.

  // If route has /tickets/:id, open that ticket when available
  useEffect(() => {
    const routeId = params?.id;
    if (!routeId) return;
    // Try to find ticket in current list first
    const tryOpen = () => {
      const t = tickets.find(x => String(x.id) === String(routeId));
      if (t) {
        setSelectedTicket(t);
        // Mark as viewed in background when opened via deep link
        (async () => { try { await apiService.markTicketAsViewed(t.id); await manualRefreshTickets(); } catch (_) {} })();
        return true;
      }
      return false;
    };
    if (!tryOpen()) {
      (async () => {
        try {
          await manualRefreshTickets();
          if (tryOpen()) return;
          // As last resort, fetch tickets directly and open
          try {
            const response = await apiService.getTickets();
            const t = (response.tickets || []).find(x => String(x.id) === String(routeId));
            if (t) {
              setSelectedTicket(t);
              try { await apiService.markTicketAsViewed(t.id); await manualRefreshTickets(); } catch (_) {}
            }
          } catch (_) {}
        } catch (_) {}
      })();
    }
  }, [params?.id, tickets, manualRefreshTickets]);

  // Check if ticket is overdue
  const isTicketOverdue = (ticket) => {
    if (!ticket.isPlanned || !ticket.plannedEndDateTime) {
      return false;
    }
    const currentTime = new Date();
    const endTime = new Date(ticket.plannedEndDateTime);
    return currentTime > endTime;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'offen': return <AlertCircle size={14} className="text-red-500" />;
      case 'in_bearbeitung': return <Clock size={14} className="text-yellow-500" />;
      case 'wartend': return <AlertTriangle size={14} className="text-orange-500" />;
      case 'zugewiesen': return <Users size={14} className="text-blue-500" />;
      case 'abgeschlossen': return <CheckCircle size={14} className="text-green-500" />;
      default: return <AlertCircle size={14} className="text-gray-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'kritisch': return 'var(--color-prio-kritisch)';
      case 'hoch': return 'var(--color-prio-hoch)';
      case 'normal': return 'var(--color-prio-normal)';
      case 'niedrig': return 'var(--color-prio-niedrig)';
      default: return 'var(--color-prio-unbekannt)';
    }
  };

  const formatLastActivity = (lastActivityAt) => {
    if (!lastActivityAt) return null;

    const now = new Date();
    const activityDate = new Date(lastActivityAt);
    const diffMs = now - activityDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;

    return `vor ${diffDays} Tag${diffDays !== 1 ? 'en' : ''}`;
  };

  // Filter tickets based on search and showCompleted setting
  const filteredTickets = React.useMemo(() => {
    let filtered = tickets;

    // Statusfilter zentral (W0-6): abgeschlossen = raus, ohne Aktivitäts-Ausnahme
    filtered = filtered.filter(ticket => !isHiddenByStatus(ticket, { showCompleted }));

    // Suche (AND-Verknüpfung zwischen Suchbegriffen, Reihenfolge egal).
    // #572: über utils/suchFilter, damit auch Kunde, Kurzform und
    // Ansprechpartner gefunden werden; Beschreibung und Kategorie wie bisher.
    if (searchTerm && searchTerm.trim() !== '') {
      filtered = filtered.filter(ticket => ticketPasstZurSuche(ticket, searchTerm, [ticket.description, ticket.category]));
    }

    return filtered;
  }, [tickets, searchTerm, showCompleted]);

  // Group tickets by assigned user
  const groupedTickets = React.useMemo(() => {
    const groups = {};
    
    filteredTickets.forEach(ticket => {
      const assignedTo = ticket.assignedTo || t('dashboard.unassigned', 'Nicht zugewiesen');
      if (!groups[assignedTo]) {
        groups[assignedTo] = [];
      }
      groups[assignedTo].push(ticket);
    });

    // Sort tickets within each group (same as DashboardTicketList)
    Object.keys(groups).forEach(userKey => {
      groups[userKey].sort((a, b) => {
        // 1. Tickets mit roter Ecke (neue Aktivität) haben Vorrang
        const aHasNewActivity = a.hasNewActivityForAssignee || a.hasNewEmailsForAssignee;
        const bHasNewActivity = b.hasNewActivityForAssignee || b.hasNewEmailsForAssignee;
        if (aHasNewActivity && !bHasNewActivity) return -1;
        if (!aHasNewActivity && bHasNewActivity) return 1;
        
        // 2. Wartend-Status nach unten (aber nur außerhalb der roten Ecke)
        if (!aHasNewActivity && !bHasNewActivity) {
          const aIsWaiting = a.status === 'wartend';
          const bIsWaiting = b.status === 'wartend';
          if (!aIsWaiting && bIsWaiting) return -1;
          if (aIsWaiting && !bIsWaiting) return 1;
        }
        
        // 3. Nach Priorität (kritisch > hoch > normal > niedrig)
        const priorityOrder = { 'kritisch': 1, 'hoch': 2, 'normal': 3, 'niedrig': 4 };
        const priorityA = priorityOrder[a.priority] || 5;
        const priorityB = priorityOrder[b.priority] || 5;
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
        
        // 4. Innerhalb gleicher Priorität: Älteste Aktivität zuerst
        const aActivityTime = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : new Date(a.createdAt || 0).getTime();
        const bActivityTime = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : new Date(b.createdAt || 0).getTime();
        return aActivityTime - bActivityTime;
      });
    });

    return groups;
  }, [filteredTickets]);

  // Get sorted user list (Nicht zugewiesen first, then alphabetically)
  const sortedUsers = React.useMemo(() => {
    const users = Object.keys(groupedTickets);
    const unassignedLabel = t('dashboard.unassigned', 'Nicht zugewiesen');
    const unassigned = users.filter(user => user === unassignedLabel);
    const assigned = users.filter(user => user !== unassignedLabel).sort();
    return [...unassigned, ...assigned];
  }, [groupedTickets]);

  // Handle ticket click (same as Dashboard)
  const handleTicketClick = useCallback(async (ticket) => {
    const currentUserEmail = currentUser?.mail || currentUser?.userPrincipalName || (typeof currentUser === 'string' ? currentUser : '');
    const isAssignee = currentUserEmail && ticket.assignedTo &&
      ticket.assignedTo.localeCompare(currentUserEmail, undefined, { sensitivity: 'base' }) === 0;

    // Optimistically remove red corner immediately
    if (isAssignee) {
      setTickets(currentTickets => currentTickets.map(t => 
        t.id === ticket.id ? { 
          ...t, 
          hasNewEmails: false,
          hasNewComments: false,
          hasNewAttachments: false,
          isNewTicket: false,
          hasNewActivity: false,
          hasNewEmailsForAssignee: false,
          hasNewCommentsForAssignee: false,
          hasNewAttachmentsForAssignee: false,
          isNewTicketForAssignee: false,
          hasNewActivityForAssignee: false
        } : t
      ));
    }
    
    // Open modal immediately
    setSelectedTicket(ticket);
    
    // Mark ticket as viewed in background
    try {
      await apiService.markTicketAsViewed(ticket.id);
      // Refresh tickets for correct sorting using auto-cache
      await manualRefreshTickets();
    } catch (error) {
      console.error('Error marking ticket as viewed:', error);
      
      // If API call fails, show red corner again (restore previous state)
      if (isAssignee) {
        setTickets(currentTickets => currentTickets.map(t => 
          t.id === ticket.id ? { ...t, hasNewActivityForAssignee: true } : t
        ));
      }
    }
  }, [manualRefreshTickets, currentUser]);

    const handleCloseTicketDetail = useCallback(async (hasChanges = false) => {
    setSelectedTicket(null);
    
    // Handle different types of changes
    if (hasChanges === 'status_uncompleted') {
      console.log('🎫 [TICKET VIEW] Ticket status changed from abgeschlossen, refreshing tickets...');
      await manualRefreshTickets();
    } else if (hasChanges === 'completed') {
      console.log('🎫 [TICKET VIEW] Ticket was completed, refreshing tickets and removing from list...');
      await manualRefreshTickets();
    } else if (hasChanges === true) {
      console.log('🎫 [TICKET VIEW] Ticket was modified, performing refresh...');
      await manualRefreshTickets();
    } else {
      console.log('🎫 [TICKET VIEW] Ticket was only viewed, no refresh needed');
    }

    // If we are on a deep link /tickets/:id, navigate back to /tickets to avoid auto-reopen loop
    try {
      if (params?.id) {
        navigate('/tickets', { replace: true });
      }
    } catch (_) {}
  }, [manualRefreshTickets]);

  const handleTicketDataUpdate = useCallback(async () => {
    // Reload tickets after update using auto-cache
    await manualRefreshTickets();
    
    // If we have a selected ticket, update it with the latest data
    if (selectedTicket) {
      try {
        const response = await apiService.getTickets();
        const updatedTicket = response.tickets.find(t => t.id === selectedTicket.id);
        if (updatedTicket) {
          setSelectedTicket(updatedTicket);
        }
      } catch (error) {
        console.error('Error updating selected ticket:', error);
      }
    }
  }, [selectedTicket, manualRefreshTickets]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (isCreatingTicket) return;
    
    if (!newTicket.title.trim()) {
      flagInvalidField(e.currentTarget, '#ticket-title', t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.')) || pushToast({ type: 'error', message: t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.') });
      return;
    }

    try {
      setIsCreatingTicket(true);
      await apiService.createTicket(newTicket);
      setShowCreateForm(false);
      setNewTicket({
        title: '',
        description: '',
        priority: 'normal',
        assignedTo: '',
        category: '',
        tags: [],
        customerId: null,
        contactId: null
      });
      await manualRefreshTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      pushToast({ type: 'error', message: t('dashboard.createTicketError', 'Fehler beim Erstellen des Tickets.') });
    } finally {
      setIsCreatingTicket(false);
    }
  };

  // Render a single ticket item (same styling as DashboardTicketList)
  const [contextMenu, setContextMenu] = useState(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSourceTicket, setMergeSourceTicket] = useState(null);

  const handleContextMenu = (e, ticket) => {
    e.preventDefault();
    setContextMenu({ ticket, position: { x: e.clientX, y: e.clientY } });
  };

  // #189: zusätzlich contextmenu (Capture) — Rechtsklick feuert kein click,
  // sonst bleibt das Menü beim Rechtsklick auf ein anderes Ticket offen.
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    document.addEventListener('click', close);
    document.addEventListener('contextmenu', close, true);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('contextmenu', close, true);
    };
  }, [contextMenu]);

  const renderTicketItem = (ticket) => (
    <div 
      key={ticket.id} 
      className="ticket-column-item"
      onClick={() => handleTicketClick(ticket)}
      onContextMenu={(e) => handleContextMenu(e, ticket)}
      style={{ cursor: 'pointer', position: 'relative' }}
    >
                    {(ticket.hasNewActivityForAssignee || ticket.hasNewEmailsForAssignee) && (
                <div 
                  className="new-activity-indicator" 
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    width: '0',
                    height: '0',
                    borderLeft: '20px solid transparent',
                    borderTop: '20px solid var(--color-danger)',
                    zIndex: 1
                  }}
                  title={(() => {
                    // Wenn das Ticket aus Sicht des Zugewiesenen komplett neu ist, zeige das an
                    if (ticket.isNewTicketForAssignee) {
                      return "Neues Ticket";
                    }
                    
                    // Ansonsten zeige spezifische neue Aktivitäten
                    const activities = [];
                    if (ticket.hasNewEmailsForAssignee) activities.push("E-Mails");
                    if (ticket.hasNewCommentsForAssignee) activities.push("Kommentare");
                    if (ticket.hasNewAttachmentsForAssignee) activities.push("Anhänge");
                    
                    if (activities.length === 0) return "Neue Aktivität";
                    if (activities.length === 1) return `Neue ${activities[0]}`;
                    if (activities.length === 2) return `Neue ${activities.join(" und ")}`;
                    return `Neue ${activities.slice(0, -1).join(", ")} und ${activities[activities.length - 1]}`;
                  })()}
                />
              )}
      {/* #102: Kunde fett über dem Ticket-Titel */}
      <CalendarTicketTitle ticket={ticket} titleClassName="ticket-column-title" />
      
      <div className="ticket-column-meta">
        <div className="ticket-status-info">
          {getStatusIcon(ticket.status)}
          <span className="ticket-status-text">{
            ticket.status === 'offen' ? t('ticket.status.open', 'Offen') :
            ticket.status === 'in_bearbeitung' ? t('ticket.status.in_progress', 'In Bearbeitung') :
            ticket.status === 'wartend' ? t('ticket.status.waiting', 'Wartend') :
            ticket.status === 'zugewiesen' ? t('ticket.status.assigned', 'Zugewiesen') :
            ticket.status === 'abgeschlossen' ? t('ticket.status.completed', 'Abgeschlossen') : ticket.status
          }</span>
        </div>
        <div className="ticket-priority-info">
          <div 
            className="ticket-priority-dot"
            style={{ backgroundColor: getPriorityColor(ticket.priority) }}
          />
          <span className="ticket-priority-text">{
            ticket.priority === 'kritisch' ? t('ticket.priority.critical', 'Kritisch') :
            ticket.priority === 'hoch' ? t('ticket.priority.high', 'Hoch') :
            ticket.priority === 'normal' ? t('ticket.priority.normal', 'Normal') :
            ticket.priority === 'niedrig' ? t('ticket.priority.low', 'Niedrig') : ticket.priority
          }</span>
        </div>
      </div>
      
      {(ticket.lastActivityAt || ticket.isPlanned || ticket.calenderMarker || ((ticket.projectId && Number(ticket.projectId) > 0) || (ticket.subprojectId && Number(ticket.subprojectId) > 0))) && (
        <div className="ticket-bottom-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {ticket.lastActivityAt && (
            <div className="ticket-last-activity" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
              <span>{formatLastActivity(ticket.lastActivityAt)}</span>
            </div>
          )}
          {(ticket.isPlanned || ticket.calenderMarker || ((ticket.projectId && Number(ticket.projectId) > 0) || (ticket.subprojectId && Number(ticket.subprojectId) > 0))) && (
            <div className="ticket-calendar" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {ticket.isPlanned && <Calendar size={12} className="text-blue-600" />}
              <CalenderMarkersDisplay markers={ticket.calenderMarker} size={12} />
              {(((ticket.projectId && Number(ticket.projectId) > 0) || (ticket.subprojectId && Number(ticket.subprojectId) > 0))) && (
                <div
                  title="Vorgangs-Ticket"
                  style={{
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    color: 'var(--color-text-black)'
                  }}
                >
                  P
                </div>
              )}
              {isTicketOverdue(ticket) && (
                <AlertTriangle size={12} className="text-red-600" fill="red" title={t('ticket.overdueTitle', 'Ticket ist überfällig')} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (loading && tickets.length === 0) {
    return (
      <div className="container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>{t('ticketSidebar.loadingTickets', 'Lade Tickets...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="ticket-view">
        <div className="ticket-header">
          <h2>
            <Ticket size={24} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
            {t('ticketView.byUserHeader', 'Tickets - Übersicht nach Benutzern')}
          </h2>
          <div className="ticket-actions">
            <button 
              onClick={() => setShowCreateForm(true)}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <Plus size={16} />
              {t('dashboard.createTicket', 'Ticket erstellen')}
            </button>
          </div>
        </div>

        {/* Search and Controls */}
        <div className="ticket-controls">
          <div className="search-bar search-bar-with-clear">
            <Search size={16} />
            <input
              type="text"
              placeholder={t('dashboard.searchTicketsPlaceholder', 'Tickets durchsuchen...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                border: 'none',
                outline: 'none',
                flex: 1,
                marginLeft: '10px',
                fontSize: '14px'
              }}
            />
            {searchTerm?.length > 0 && (
              <button
                type="button"
                className="clear-input-btn"
                onClick={(e) => {
                  try {
                    setSearchTerm('');
                    // keep focus on the input for quick retyping
                    const input = (e?.currentTarget?.parentElement || document).querySelector('input[type="text"]');
                    if (input) input.focus();
                  } catch (_) {
                    try { const el = document.querySelector('.search-bar-with-clear input[type="text"]'); if (el) el.focus(); } catch (_) {}
                  }
                }}
                title={t('common.clear', 'Leeren')}
                aria-label={t('common.clear', 'Leeren')}
              >
                <X size={16} />
              </button>
            )}
          </div>

          <button 
            onClick={() => setShowCompleted(!showCompleted)}
            className={`btn ${showCompleted ? 'btn-primary' : 'btn-secondary'}`}
            style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            <CheckCircle size={16} />
            {t('ticketView.includeCompleted', 'Auch Abgeschlossene')}
          </button>
        </div>

        {/* Column-based Ticket Layout */}
        <div className="ticket-columns-container">
          {sortedUsers.length === 0 ? (
            <div className="ticket-empty-state">
              <Ticket size={32} style={{ opacity: 0.3 }} />
              <p>{t('common.noTicketsFound', 'Keine Tickets gefunden')}</p>
              {searchTerm && (
                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-placeholder)', marginTop: '8px' }}>
                  {t('common.tryOtherSearchTerms', 'Versuchen Sie andere Suchbegriffe')}
                </p>
              )}
            </div>
          ) : (
            <div className="ticket-columns">
              {sortedUsers.map(user => (
                <div key={user} className="ticket-column">
                  <div className="ticket-column-header">
                    <h3>
                      {user === 'Nicht zugewiesen' ? (
                        <>
                          <AlertCircle size={16} />
                          {user}
                        </>
                      ) : (
                        <>
                          <Users size={16} />
                          {user}
                        </>
                      )}
                    </h3>
                    <span className="ticket-count">
                      {groupedTickets[user]?.length || 0}
                    </span>
                  </div>
                  <div className="ticket-column-content">
                    {groupedTickets[user]?.map(ticket => renderTicketItem(ticket))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ticket Detail Modal */}
        {selectedTicket && (
          <TicketDetail 
          isMobileView={isMobileScreen}
            ticket={selectedTicket}
            onClose={handleCloseTicketDetail}
            onTicketUpdate={handleTicketDataUpdate}
          />
        )}

        {/* Create Ticket Modal */}
          <Modal
            open={showCreateForm}
            onClose={() => setShowCreateForm(false)}
            title={<>
              <Ticket size={24} />
              <h3>{t('dashboard.createTicketHeader', 'Neues Ticket erstellen')}</h3>
              <CalenderMarkerHeaderSelect value={newTicket.calenderMarker} onChange={(value) => setNewTicket({...newTicket, calenderMarker: value})} />
            </>}
            ariaLabel={t('dashboard.createTicketHeader', 'Neues Ticket erstellen')}
            footer={<>
              <button type="button" onClick={() => setShowCreateForm(false)} className="btn btn-secondary">{t('dashboard.cancel', 'Abbrechen')}</button>
              <button type="submit" form="ticketview-create-ticket-form" className="btn btn-primary" disabled={isCreatingTicket}><Plus size={16} />{isCreatingTicket ? t('common.creating', 'Erstelle...') : t('dashboard.createTicket', 'Ticket erstellen')}</button>
            </>}
          >
              
              <form id="ticketview-create-ticket-form" onSubmit={handleCreateTicket} className="create-ticket-form">
                <div className="form-body">
                  {/* #104: Kunde (halbbreit) über dem Titel */}
                  <TicketCustomerField
                    value={newTicket.customerId}
                    onChange={(customerId) => setNewTicket(prev => ({...prev, customerId}))}
                    contactValue={newTicket.contactId}
                    onContactChange={(contactId) => setNewTicket(prev => ({...prev, contactId}))}
                    inputId="ticket-customer"
                  />
                  <div className="form-group">
                    <label htmlFor="ticket-title">{t('dashboard.field.title', 'Titel')} <span className="required">*</span></label>
                    <input
                      id="ticket-title"
                      type="text"
                      value={newTicket.title}
                      onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                      required
                      placeholder={t('dashboard.placeholder.title', 'Ticket-Titel eingeben...')}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="ticket-description">{t('dashboard.field.description', 'Beschreibung')}</label>
                    <textarea
                      id="ticket-description"
                      value={newTicket.description}
                      onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                      placeholder={t('dashboard.placeholder.description', 'Ticket-Beschreibung eingeben...')}
                      rows="4"
                      className="form-textarea"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="ticket-priority">{t('dashboard.field.priority', 'Priorität')}</label>
                      <select
                        id="ticket-priority"
                        value={newTicket.priority}
                        onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                        className="form-select"
                      >
                        <option value="niedrig">{t('ticket.priority.low', 'Niedrig')}</option>
                        <option value="normal">{t('ticket.priority.normal', 'Normal')}</option>
                        <option value="hoch">{t('ticket.priority.high', 'Hoch')}</option>
                        <option value="kritisch">{t('ticket.priority.critical', 'Kritisch')}</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="ticket-assigned">{t('dashboard.field.assignedTo', 'Zugewiesen an')}</label>
                      <select
                        id="ticket-assigned"
                        value={newTicket.assignedTo}
                        onChange={(e) => setNewTicket({...newTicket, assignedTo: e.target.value})}
                        className="form-select"
                      >
                        <option value="">{t('dashboard.unassigned', 'Nicht zugewiesen')}</option>
                        {availableEmails.map(email => (
                          <option key={email} value={email}>{email}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  
                  

                </div>
                
            </form>
          </Modal>
      </div>
      {contextMenu && (
        <TicketContextMenu 
          ticket={contextMenu.ticket}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          onTicketUpdate={async () => { await manualRefreshTickets(); }}
          onOpenMerge={(ticket) => { setMergeSourceTicket(ticket); setShowMergeModal(true); }}
        />
      )}
      {showMergeModal && (
        <TicketMergeModal 
          isOpen={showMergeModal}
          sourceTicket={mergeSourceTicket}
          onClose={() => setShowMergeModal(false)}
          onMerged={async () => { await manualRefreshTickets(); }}
        />
      )}
    </div>
  );
};

export default TicketView; 